import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal, User as UserIcon, ScrollText, BadgeCheck, OctagonAlert, Plus, MessageSquarePlus,
  Milestone, StickyNote, CalendarClock, History as HistoryIcon,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, initialsOf } from "@/lib/utils";
import { fmtDateFr } from "@/components/placements/placementsShared";
import { AddInteractionDialog } from "@/components/placements/WorkspaceTabs";
import { DSTATE } from "./discussionsShared";
import ProgressionBar from "./ProgressionBar";
import {
  SituationCard, NextActionCard, InterlocutorsCard, ExpectationsCard,
  AttentionPointsCard, TermsCard, ContractContextCard,
} from "./DiscussionCards";
import { HistoryTab, DocumentsTab, NotesTab, ActionsTab, ContexteTab } from "./DiscussionTabs";
import { ConfirmMandateDialog, AddActionDialog, AddInterlocutorDialog, TermsDialog, StageChangeDialog } from "./DiscussionDialogs";
import { useLang } from "@/i18n";

export default function DiscussionDetail({ detail, onRefresh }) {
  const { tf } = useLang();
  const navigate = useNavigate();
  const [tabVal, setTabVal] = useState("historique");
  const [confirming, setConfirming] = useState(false);
  const [planAction, setPlanAction] = useState(false);
  const [addPerson, setAddPerson] = useState(false);
  const [editTerms, setEditTerms] = useState(false);
  const [addInteraction, setAddInteraction] = useState(false);
  const [stageDialog, setStageDialog] = useState({ open: false, stage: null });
  const a = detail.athlete || {};
  const st = DSTATE[detail.state?.state] || DSTATE.ACTIVE;
  const isOpen = detail.status === "OPEN";
  const mandateSignedButOpen = isOpen && a.status === "Mandat signé";

  const DETAIL_TABS = [
    { v: "historique", l: "Historique", count: (detail.history || []).length + (detail.interactions || []).length },
    { v: "documents", l: "Documents", count: (detail.documents || []).length },
    { v: "notes", l: "Notes", count: (detail.notes || []).length },
    { v: "actions", l: tf("Prochaines actions"), count: (detail.open_actions || []).length },
    { v: "contexte", l: "Contexte", count: null },
  ];

  const toggleBlocked = async () => {
    try {
      const blocked = !detail.blocked;
      const reason = blocked ? window.prompt(tf("Motif du blocage (optionnel)")) : null;
      await api.patch(`/discussions/${detail.id}`, { blocked, blocked_reason: reason || null });
      toast.success(blocked ? tf("Discussion marquée bloquée") : tf("Blocage levé"));
      onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };

  return (
    <div className="min-w-0" data-testid="discussion-detail">
      {/* Header */}
      <div className="flex items-start gap-3.5">
        <Avatar className="h-14 w-14 shrink-0">
          <AvatarImage src={a.photo_url || undefined} alt="" />
          <AvatarFallback className="text-base font-semibold bg-secondary">{initialsOf(a.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-xl font-bold tracking-tight truncate" data-testid="discussion-athlete-name">{a.name}</h2>
            <span className={cn("text-[10px] font-medium rounded-full border px-2 py-0.5", st.chip)}
              data-testid="discussion-state-badge">
              {isOpen ? tf(st.label) : detail.status === "COMPLETED" ? tf("Mandat confirmé") : tf("Clôturée")}
            </span>
          </div>
          <div className="text-sm text-muted-foreground truncate mt-0.5">
            {[a.club, a.position, a.age ? `${a.age} ans` : null, a.nationality].filter(Boolean).join(" · ") || "—"}
          </div>
          <div className="text-xs text-muted-foreground/80 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="rounded-full border border-info/30 bg-info/10 text-info px-2 py-0.5 text-[10px] font-medium">
              {tf(a.status || "En discussion")}
            </span>
            <span>{tf("Depuis le")} {fmtDateFr(detail.started_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isOpen && (
            <Button onClick={() => setConfirming(true)}
              className="hidden sm:inline-flex h-9 gap-1.5 bg-info hover:bg-info/90 text-white"
              data-testid="confirm-mandate-cta">
              <BadgeCheck className="h-4 w-4" /> {tf("Confirmer le mandat")}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full p-2 hover:bg-accent dark:hover:bg-white/[0.06] text-muted-foreground hover:text-foreground transition-colors"
                data-testid="discussion-menu" aria-label={tf("Actions de la discussion")}>
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={() => navigate(`/players/${a.id}`)} data-testid="menu-open-athlete">
                <UserIcon className="h-4 w-4 mr-2" /> {tf("Voir le profil")}
              </DropdownMenuItem>
              {detail.contract?.id && (
                <DropdownMenuItem onClick={() => navigate(`/contracts/${detail.contract.id}`)} data-testid="menu-open-contract">
                  <ScrollText className="h-4 w-4 mr-2" /> {tf("Voir le contrat")}
                </DropdownMenuItem>
              )}
              {isOpen && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setStageDialog({ open: true, stage: null })} data-testid="menu-change-stage">
                    <Milestone className="h-4 w-4 mr-2" /> {tf("Modifier l'étape")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAddInteraction(true)} data-testid="menu-add-note">
                    <StickyNote className="h-4 w-4 mr-2" /> {tf("Ajouter une note")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPlanAction(true)} data-testid="menu-plan-action">
                    <CalendarClock className="h-4 w-4 mr-2" /> {tf("Planifier une prochaine action")}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setTabVal("historique")} data-testid="menu-view-history">
                <HistoryIcon className="h-4 w-4 mr-2" /> {tf("Voir l'historique")}
              </DropdownMenuItem>
              {isOpen && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={toggleBlocked} className={detail.blocked ? "" : "text-athlynx focus:text-athlynx"}
                    data-testid="menu-toggle-blocked">
                    <OctagonAlert className="h-4 w-4 mr-2" />
                    {detail.blocked ? tf("Lever le blocage") : tf("Marquer comme bloquée")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isOpen && (
        <Button onClick={() => setConfirming(true)}
          className="sm:hidden w-full mt-3 h-10 gap-1.5 bg-info hover:bg-info/90 text-white"
          data-testid="confirm-mandate-cta-mobile">
          <BadgeCheck className="h-4 w-4" /> {tf("Confirmer le mandat")}
        </Button>
      )}

      {detail.blocked && detail.blocked_reason && (
        <div className="mt-3 rounded-xl border border-athlynx/25 bg-athlynx/[0.04] px-3 py-2 text-xs text-athlynx flex items-start gap-2"
          data-testid="blocked-banner">
          <OctagonAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {detail.blocked_reason}
        </div>
      )}

      {mandateSignedButOpen && (
        <div className="mt-3 rounded-xl border border-info/25 bg-info/[0.05] px-3 py-2.5 text-xs flex flex-wrap items-center gap-2"
          data-testid="mandate-sync-banner">
          <BadgeCheck className="h-3.5 w-3.5 text-info shrink-0" />
          <span className="text-foreground/90 flex-1 min-w-[200px]">
            {tf("L'athlète a un mandat signé mais cette discussion est toujours ouverte. Vous pouvez la clôturer pour aligner le suivi.")}
          </span>
          <Button size="sm" variant="outline" onClick={() => setConfirming(true)}
            className="h-7 text-xs border-info/40 text-info hover:bg-info/10"
            data-testid="mandate-sync-close-cta">
            {tf("Clôturer la discussion")}
          </Button>
        </div>
      )}

      {/* Progression */}
      <ProgressionBar detail={detail} onStageSelect={(stage) => setStageDialog({ open: true, stage })} />

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <SituationCard detail={detail} onRefresh={onRefresh} />
        <NextActionCard detail={detail} onRefresh={onRefresh} onPlan={() => setPlanAction(true)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 mt-4">
        <InterlocutorsCard detail={detail} onRefresh={onRefresh} onAdd={() => setAddPerson(true)} />
        <ExpectationsCard detail={detail} onRefresh={onRefresh} />
        <AttentionPointsCard detail={detail} onRefresh={onRefresh} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <TermsCard detail={detail} onEdit={() => setEditTerms(true)} />
        <ContractContextCard detail={detail} />
      </div>

      {/* Lower tabs — compact Select on mobile, wrapping bar from sm up */}
      <div className="flex items-center justify-between gap-2 mt-6">
        <div className="sm:hidden flex-1 min-w-0">
          <Select value={tabVal} onValueChange={setTabVal}>
            <SelectTrigger className="h-10 rounded-xl border-border bg-card text-sm font-medium" data-testid="discussion-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DETAIL_TABS.map((t) => (
                <SelectItem key={t.v} value={t.v}>
                  {tf(t.l)}{t.count != null && t.count > 0 ? ` (${t.count})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isOpen && (
          <Button size="sm" onClick={() => setAddInteraction(true)}
            className="h-9 gap-1.5 bg-info hover:bg-info/90 text-white shrink-0 sm:order-2"
            data-testid="add-interaction-cta">
            <Plus className="h-3.5 w-3.5" /> <span className="hidden min-[420px]:inline">{tf("Ajouter une interaction")}</span>
            <MessageSquarePlus className="h-3.5 w-3.5 min-[420px]:hidden" />
          </Button>
        )}
      </div>
      <Tabs value={tabVal} onValueChange={setTabVal} className="mt-1 sm:mt-2">
        <TabsList className="hidden sm:flex flex-wrap bg-transparent border-b border-border dark:border-white/[0.06] rounded-none w-full justify-start h-auto p-0 gap-0">
          {DETAIL_TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`discussion-tab-${t.v}`}
              className="flex-none rounded-none -mb-px border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:bg-transparent text-muted-foreground px-3 md:px-4 py-2.5 text-xs md:text-sm whitespace-nowrap">
              {tf(t.l)}
              {t.count != null && t.count > 0 && (
                <span className="ml-1.5 tabular-nums rounded-md bg-black/[0.05] dark:bg-white/[0.08] px-1.5 py-0.5 text-[10px]">
                  {t.count}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="historique" className="mt-4"><HistoryTab detail={detail} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab detail={detail} /></TabsContent>
        <TabsContent value="notes" className="mt-4"><NotesTab detail={detail} onAddInteraction={() => setAddInteraction(true)} /></TabsContent>
        <TabsContent value="actions" className="mt-4"><ActionsTab detail={detail} onRefresh={onRefresh} onPlan={() => setPlanAction(true)} /></TabsContent>
        <TabsContent value="contexte" className="mt-4"><ContexteTab detail={detail} onRefresh={onRefresh} /></TabsContent>
      </Tabs>

      <ConfirmMandateDialog open={confirming} onOpenChange={setConfirming} detail={detail} onConfirmed={onRefresh} />
      <StageChangeDialog open={stageDialog.open} onOpenChange={(v) => setStageDialog((s) => ({ ...s, open: v }))}
        detail={detail} initialStage={stageDialog.stage} onSaved={onRefresh} />
      <AddActionDialog open={planAction} onOpenChange={setPlanAction} discussionId={detail.id} onSaved={onRefresh} />
      <AddInterlocutorDialog open={addPerson} onOpenChange={setAddPerson} discussionId={detail.id} onSaved={onRefresh} />
      <TermsDialog open={editTerms} onOpenChange={setEditTerms} detail={detail} onSaved={onRefresh} />
      <AddInteractionDialog open={addInteraction} onOpenChange={setAddInteraction} playerId={a.id} onSaved={onRefresh} />
    </div>
  );
}
